// The bulk bar's actions (#668, spec docs/specs/web/S1c-library-move-bulk.md):
// - Move to project: live's picker at "All assets & files", one call for every file;
// - Download: one `downloads.zip`, which the worker makes (spec bulk-zip), then live's words;
// - Delete: after live's "Delete N assets", each file through the library's Delete. D6 applies
//   file by file, and a file that is not deleted is counted and said why, in one line
//   (web/AGENTS.md: no toasts). The trash keeps them `TrashSummary.days`, as the single Delete says.
// - Re-index (spec S3, live's ⋯): each file indexed for search again, one call each as live's,
//   then live's line of how many went ("2 from 2 assets re-index successfully."), which goes after
//   live's toast's 5 s (react-toastify's default).
// Each action clears the selection once it ran. A line goes when another action starts or the
// list shows another view (a folder, a filter, a search).
import { Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { downloadSignedFile } from "../../api/download";
import { ApiError, isApiError } from "../../api/errors";
import { deleteAsset, reindexAsset } from "../../api/generated/endpoints/assets/assets";
import type { Asset } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { refreshAccessScope } from "../../api/session";
import { toUserMessage } from "../../platform/messages";
import { MoveToProject } from "../../platform/media/MoveToProject";
import { usePrefetchTrashDays, useTrashStay } from "../../platform/media/trash-days";
import { ConfirmModal } from "../../ui/ConfirmModal";
import type { BulkBarProps } from "./BulkBar";
import { ZIP_NAME, zipAttempt, zipLeft, zipped, type ZipAttempt } from "./bulk-zip";

const files = (count: number) => (count === 1 ? "1 file" : `${count} files`);

/** How long live's Re-index toast stays (react-toastify's `autoClose`). */
export const REINDEX_NOTE_MS = 5000;

/** A line under the toolbar: the view it was said in, and whether it goes by itself, as live's toast. */
interface Note {
  text: string;
  view: string;
  fades: boolean;
}

/** Why some files were not deleted, as one sentence. */
export function notDeleted(
  asked: number,
  refused: { theirs: number; used: number; other: number },
) {
  const total = refused.theirs + refused.used + refused.other;
  const why = [
    refused.theirs > 0 ? `${files(refused.theirs)} only their creator or an admin may delete` : "",
    refused.used > 0 ? `${files(refused.used)} in use as a brand logo or a product image` : "",
    refused.other > 0 ? `${files(refused.other)} that could not be deleted, please try again` : "",
  ].filter((part) => part !== "");
  return `${total} of ${asked} files were not deleted: ${why.join("; ")}.`;
}

/** Live's words after a Re-index: what went, and what did not (live's stray ")" left out). */
export function reindexed(done: number, asked: number): string {
  const failed = asked - done;
  return [
    done > 0 ? `${done} from ${asked} assets re-index successfully.` : "",
    failed > 0 ? `${failed} from ${asked} assets re-index failed.` : "",
  ]
    .filter((part) => part !== "")
    .join(" ");
}

export interface BulkActions {
  bar: Omit<BulkBarProps, "count" | "onClear">;
  /** The Move and Delete dialogs. */
  dialogs: ReactNode;
  /** What the last action could not do, in a sentence; null when it all went. */
  note: string | null;
}

export function useBulkActions(
  teamId: string,
  brandId: string,
  selected: readonly Asset[],
  clear: () => void,
  /** The list's view: another one lets the last line go. */
  view: string,
): BulkActions {
  const queryClient = useQueryClient();
  // Through a mutation, so an ended session, at the start or at a later read of the job, reaches
  // the shell's one handler (F3), as the upload queue's files do.
  const zipping = useMutation({
    mutationFn: ({ assetIds, attempt }: { assetIds: string[]; attempt: ZipAttempt }) =>
      zipped(teamId, brandId, assetIds, attempt),
  });
  // What a failed Download leaves for the next one of the same files (`zipLeft`).
  const left = useRef<ZipAttempt | undefined>(undefined);
  const [moving, setMoving] = useState(false);
  const [asking, setAsking] = useState(false);
  const stay = useTrashStay(teamId, brandId, asking);
  // The bulk bar's Delete shows with a selection: its question's days are read then.
  const prefetchDays = usePrefetchTrashDays(teamId, brandId);
  const selecting = selected.length > 0;
  useEffect(() => {
    if (selecting) prefetchDays();
  }, [selecting, prefetchDays]);
  const [busy, setBusy] = useState<BulkBarProps["busy"]>(null);
  const [note, setNote] = useState<Note | null>(null);
  const say = (text: string, fades = false) => setNote({ text, view, fades });
  useEffect(() => {
    if (note === null || !note.fades) return;
    const timer = window.setTimeout(() => setNote(null), REINDEX_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [note]);

  const download = async () => {
    setNote(null);
    setBusy("download");
    const assetIds = selected.map((asset) => asset.id);
    // What a key stands for: these files, of this team and brand.
    const asked = { teamId, brandId, assetIds };
    const attempt = zipAttempt(asked, left.current);
    left.current = undefined;
    try {
      const zip = await zipping.mutateAsync({ assetIds, attempt });
      await downloadSignedFile(zip.url, ZIP_NAME);
      clear();
      // Live's toasts: what the ZIP holds, for its 5 s; and, kept, the files it left out.
      const made = `Downloaded ${zip.files} file(s) as ZIP`;
      const all = zip.files + zip.skipped;
      if (zip.skipped === 0) say(made, true);
      else say(`${made}. Failed to download ${zip.skipped} of ${all} files.`);
    } catch (error) {
      // While the ZIP may be the server's, Download again on these files sends its key again and
      // gets that ZIP rather than a second one (spec bulk-zip decision 3).
      left.current = zipLeft(attempt, asked, error);
      // A refused start says why (over 10 GB, the hourly cap); a failed ZIP or save, live's
      // words. The selection stays, so it can be asked again (spec bulk-zip decision 1).
      say(error instanceof ApiError ? toUserMessage(error) : "Failed to download asset. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setAsking(false);
    setNote(null);
    setBusy("delete");
    const refused = { theirs: 0, used: 0, other: 0 };
    for (const asset of selected) {
      try {
        await deleteAsset(teamId, brandId, asset.id);
      } catch (error) {
        if (isApiError(error, "PERMISSION_DENIED")) refused.theirs += 1;
        else if (isApiError(error, "ASSET_IN_USE")) refused.used += 1;
        else refused.other += 1;
      }
    }
    const anyRefused = refused.theirs + refused.used + refused.other > 0;
    await Promise.all([
      refreshLibraryLists(queryClient, teamId, brandId),
      // A refusal may mean the hint that offered Delete is stale.
      anyRefused ? refreshAccessScope(queryClient) : Promise.resolve(),
    ]);
    setBusy(null);
    clear();
    if (anyRefused) say(notDeleted(selected.length, refused));
  };

  const reindex = async () => {
    setNote(null);
    setBusy("reindex");
    let done = 0;
    for (const asset of selected) {
      try {
        await reindexAsset(teamId, brandId, asset.id);
        done += 1;
      } catch {
        // Counted below, as live's.
      }
    }
    await refreshLibraryLists(queryClient, teamId, brandId);
    setBusy(null);
    clear();
    say(reindexed(done, selected.length), true);
  };

  const dialogs = (
    <>
      <MoveToProject
        teamId={teamId}
        brandId={brandId}
        opened={moving}
        assetIds={selected.map((asset) => asset.id)}
        onClose={() => setMoving(false)}
        onMoved={clear}
      />
      <ConfirmModal
        opened={asking}
        title={`Delete ${selected.length} assets`}
        confirmLabel="Move to trash"
        onConfirm={() => void remove()}
        onCancel={() => setAsking(false)}
      >
        <Text size="sm">
          They stay {stay}, where anyone who may delete them can restore them.
        </Text>
      </ConfirmModal>
    </>
  );

  return {
    bar: {
      busy,
      onMove: () => {
        setNote(null);
        setMoving(true);
      },
      onDownload: () => void download(),
      onDelete: () => {
        setNote(null);
        setAsking(true);
      },
      onReindex: () => void reindex(),
    },
    dialogs,
    note: note !== null && note.view === view ? note.text : null,
  };
}
